import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, LockKey, DeviceMobile, Warning } from "@phosphor-icons/react";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [nextConfirm, setNextConfirm] = useState("");

  const loadStatus = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };
  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error("Kurulum başlatılamadı"); }
  };

  const enable = async () => {
    if (otpCode.length !== 6) return toast.error("6 haneli kodu girin");
    try {
      await api.post("/auth/2fa/enable", { code: otpCode });
      toast.success("2FA etkinleştirildi");
      setSetup(null); setOtpCode("");
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kod doğrulanamadı"); }
  };

  const disable = async () => {
    if (!window.confirm("2FA'yı devre dışı bırakmak istediğinize emin misiniz?")) return;
    try {
      await api.post("/auth/2fa/disable");
      toast.success("2FA devre dışı bırakıldı");
      loadStatus();
    } catch (e) { toast.error("Devre dışı bırakılamadı"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (next.length < 10) return toast.error("Yeni şifre en az 10 karakter olmalı (harf + rakam)");
    if (next !== nextConfirm) return toast.error("Şifreler eşleşmiyor");
    try {
      await api.post("/auth/change-password", { current_password: curr, new_password: next });
      toast.success("Şifreniz güncellendi");
      setCurr(""); setNext(""); setNextConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Şifre değiştirilemedi"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Hesap</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Güvenlik</h1>
      </div>

      {/* Change Password */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <LockKey size={22} weight="fill" className="text-primary" />
            <h3 className="font-semibold uppercase tracking-widest text-sm">Şifre Değiştir</h3>
          </div>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label>Mevcut Şifre</Label>
              <Input type="password" value={curr} onChange={(e) => setCurr(e.target.value)}
                     className="rounded-sm" required data-testid="sec-current-pw" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Yeni Şifre</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                       className="rounded-sm" required minLength={6} data-testid="sec-new-pw" />
              </div>
              <div>
                <Label>Yeni Şifre (Tekrar)</Label>
                <Input type="password" value={nextConfirm} onChange={(e) => setNextConfirm(e.target.value)}
                       className="rounded-sm" required minLength={6} data-testid="sec-new-pw-confirm" />
              </div>
            </div>
            <Button type="submit" data-testid="sec-change-pw-btn"
                    className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Şifreyi Güncelle
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* 2FA */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} weight="fill" className="text-primary" />
              <h3 className="font-semibold uppercase tracking-widest text-sm">
                İki Adımlı Doğrulama (2FA)
              </h3>
            </div>
            {status && (
              <Badge variant={status.enabled ? "default" : "secondary"}
                     className="rounded-sm text-[10px] uppercase tracking-widest"
                     data-testid="sec-2fa-status">
                {status.enabled ? "Aktif" : "Kapalı"}
              </Badge>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            Hesabınıza fazladan bir güvenlik katmanı ekleyin. Google Authenticator,
            Microsoft Authenticator veya benzeri bir uygulama gereklidir.
          </p>

          {!status?.enabled && !setup && (
            <Button onClick={beginSetup} data-testid="sec-2fa-setup-btn"
                    className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              <DeviceMobile size={16} /> 2FA Kurulumunu Başlat
            </Button>
          )}

          {setup && (
            <div className="space-y-4 border border-primary/30 bg-primary/5 rounded-sm p-4">
              <div className="text-xs uppercase tracking-widest font-semibold">
                1. Authenticator uygulamanızla QR kodu tarayın
              </div>
              <div className="flex justify-center">
                <img src={setup.qr_data_url} alt="2FA QR" width={200} height={200}
                     className="border border-border rounded-sm" data-testid="sec-2fa-qr" />
              </div>
              <div className="text-xs text-center text-muted-foreground">
                QR kodu tarayamıyorsanız bu kodu manuel girin:
                <div className="font-mono text-sm mt-1 bg-muted px-2 py-1 rounded-sm inline-block"
                     data-testid="sec-2fa-secret">{setup.secret}</div>
              </div>
              <div>
                <Label>2. Uygulamada gösterilen 6 haneli kodu girin</Label>
                <Input value={otpCode}
                       onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       className="rounded-sm font-mono text-lg tracking-widest text-center"
                       placeholder="123456"
                       data-testid="sec-2fa-code" />
              </div>
              <div className="flex gap-2">
                <Button onClick={enable} data-testid="sec-2fa-enable-btn"
                        className="flex-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                  Etkinleştir
                </Button>
                <Button variant="outline" onClick={() => { setSetup(null); setOtpCode(""); }}
                        className="rounded-sm uppercase tracking-widest">
                  İptal
                </Button>
              </div>
            </div>
          )}

          {status?.enabled && !setup && (
            <>
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-sm">
                <ShieldCheck size={18} weight="fill" className="text-green-700" />
                <div className="text-sm text-green-800">
                  2FA aktif. Her girişte 6 haneli kod istenecek.
                </div>
              </div>
              <Button variant="outline" onClick={disable} data-testid="sec-2fa-disable-btn"
                      className="rounded-sm uppercase tracking-widest font-semibold text-destructive border-destructive/40 hover:bg-destructive/5">
                <Warning size={16} /> 2FA'yı Devre Dışı Bırak
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
